import { FC, useEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';

type DropdownSubPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  triggerElement: HTMLElement | null;
  parentDropdownOpen: boolean;
  onParentDropdownChange: (open: boolean) => void;
  children: ReactNode;
  zIndex?: number;
  backdropZIndex?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

/**
 * A reusable subpanel component that renders outside of Ant Design Dropdown menus
 * to avoid submenu bugs. Handles positioning, backdrop, and event propagation.
 * 
 * IMPORTANT: If your component has custom click-outside handlers (e.g., for closing
 * dropdowns), you must check if clicks are inside subpanels to prevent accidentally
 * closing the dropdown when users interact with subpanels.
 * 
 * Use the `isClickInsideSubpanel()` utility function from `./utils/subpanelUtils` to
 * check if a click target is inside any subpanel. Alternatively, you can check for
 * the `.dropdown-subpanel-wrapper` class directly.
 * 
 * Z-INDEX: The component uses default z-index values (1051 for panel, 1050 for backdrop)
 * which should be higher than Ant Design's default dropdown z-index (typically 1050).
 * If you encounter z-index conflicts where the subpanel appears behind the parent dropdown,
 * you can override the `zIndex` and `backdropZIndex` props. The subpanel should always
 * have a higher z-index than the parent dropdown to appear on top.
 * 
 * HOVER BEHAVIOR: To enable hover-to-open behavior (like Ant Design's native submenus),
 * you can pass `onMouseEnter` and `onMouseLeave` handlers. The typical pattern is:
 * - Use a ref to store a timeout (e.g., `const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)`)
 * - On trigger `onMouseEnter`: Clear any pending timeout and call `panel.open()`
 * - On trigger `onMouseLeave`: Set a timeout (150ms) to call `panel.close()`
 * - On panel `onMouseEnter`: Clear any pending timeout to keep panel open
 * - On panel `onMouseLeave`: Set a timeout to close the panel
 * - Clean up the timeout in a `useEffect` cleanup function
 * 
 * See `RecordingActions.tsx` or `WorkActions.tsx` for complete hover implementation examples.
 * 
 * @param zIndex - Z-index for the subpanel (default: 1051). Should be higher than parent dropdown.
 * @param backdropZIndex - Z-index for the backdrop (default: 1050). Should be between dropdown and panel.
 * @param onMouseEnter - Optional handler for when mouse enters the panel (for hover behavior)
 * @param onMouseLeave - Optional handler for when mouse leaves the panel (for hover behavior)
 * 
 * @example
 * ```typescript
 * const handleClickOutside = (event: Event) => {
 *   const target = event.target as HTMLElement;
 *   if (isClickInsideSubpanel(target)) return; // Don't close if clicking inside subpanel
 *   // ... close dropdown logic
 * };
 * 
 * // If z-index conflicts occur:
 * <DropdownSubPanel
 *   zIndex={1060}  // Higher than default if needed
 *   backdropZIndex={1059}
 *   // ... other props
 * />
 * ```
 */
export const DropdownSubPanel: FC<DropdownSubPanelProps> = ({
  isOpen,
  onClose,
  triggerElement,
  parentDropdownOpen,
  onParentDropdownChange,
  children,
  zIndex = 1051,
  backdropZIndex = 1050,
  onMouseEnter,
  onMouseLeave,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Calculate position from trigger element and clamp within viewport
  useEffect(() => {
    if (!isOpen || !triggerElement) {
      setPosition(null);
      return;
    }

    let frame: number | null = null;
    const computePosition = () => {
      const rect = triggerElement.getBoundingClientRect();
      const panel = panelRef.current;
      const panelWidth = panel?.offsetWidth ?? 0;
      const panelHeight = panel?.offsetHeight ?? 0;
      const padding = 8; // avoid hugging the viewport edges

      let top = rect.bottom;
      let left = rect.right;

      // If the panel would overflow bottom, prefer placing it above
      if (top + panelHeight > window.innerHeight - padding) {
        top = rect.top - panelHeight;
      }
      // Clamp to viewport
      top = Math.max(padding, Math.min(top, window.innerHeight - padding));

      // If the panel would overflow right, prefer placing it to the left
      if (left + panelWidth > window.innerWidth - padding) {
        left = rect.left - panelWidth;
      }
      left = Math.max(padding, Math.min(left, window.innerWidth - padding));

      setPosition({ top, left });
    };

    const handleSchedule = () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      frame = window.requestAnimationFrame(computePosition);
    };

    frame = window.requestAnimationFrame(computePosition);
    const handleResize = () => handleSchedule();
    const handleScroll = () => handleSchedule();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, triggerElement]);

  // Prevent dropdown from closing when clicking on trigger element or subpanel
  useEffect(() => {
    if (!parentDropdownOpen) return;

    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Prevent dropdown from closing when clicking on trigger element
      if (triggerElement && triggerElement.contains(target)) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }

      // Prevent dropdown from closing when clicking on subpanel (non-interactive elements only)
      if (isOpen && panelRef.current && panelRef.current.contains(target)) {
        // Only stop propagation for non-interactive elements
        // Allow buttons, inputs, and other interactive elements to work normally
        const isInteractive =
          target.tagName === 'BUTTON' ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.closest('button') ||
          target.closest('input') ||
          target.closest('[role="button"]');

        if (!isInteractive) {
          e.stopPropagation();
        }
      }
    };

    // Use capture phase to intercept before Ant Design's handlers
    document.addEventListener('mousedown', handleDocumentMouseDown, true);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, [parentDropdownOpen, isOpen, triggerElement]);


  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const resolvedTop = position?.top ?? 0;
  const resolvedLeft = position?.left ?? 0;
  const isMeasured = Boolean(position);

  return createPortal(
    <>
      {/* Backdrop to close both panels on outside click */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: backdropZIndex,
        }}
        onClick={(e) => {
          // Only close if clicking the backdrop itself (not panel or dropdown)
          if (e.target === e.currentTarget) {
            onClose();
            onParentDropdownChange(false);
          }
        }}
      />
      {/* 
        IMPORTANT: This className is used by isClickInsideSubpanel() utility
        to identify clicks inside subpanels. Custom click-outside handlers should
        check for this class or use the utility function.
      */}
      <div
        ref={panelRef}
        className='dropdown-subpanel-wrapper'
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          position: 'fixed',
          top: `${resolvedTop}px`,
          left: `${resolvedLeft}px`,
          zIndex: zIndex,
          padding: '8px',
          backgroundColor: '#282828',
          fontSize: '14px',
          borderRadius: '4px',
          boxShadow: '0 16px 24px rgba(0, 0, 0, 0.3), 0 6px 8px rgba(0, 0, 0, 0.2)',
          visibility: isMeasured ? 'visible' : 'hidden',
          pointerEvents: isMeasured ? 'auto' : 'none',
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
};
